import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Button, Card } from 'react-bootstrap';
import { useLocation, useNavigate } from 'react-router-dom';
import { cameFromState } from '@/features/applications/faculty/backLink';
import CourseTypeahead from './CourseTypeahead';

/**
 * Starting a course support application from nothing but the course's name.
 *
 * Naming the course is the whole of what the form needs to start, so this
 * panel asks for that and nothing else. It belongs where there is no catalog
 * on screen: with a grid of courses below, the grid's own search and each
 * card's Request Support button do the same job in fewer steps, and a second
 * box searching the same courses only raises the question of which one to
 * use. Both routes open the same step 1 with the same parameter, so neither
 * is the primary one.
 */
function StartApplicationPanel({
    termCode,
    heading = 'Start a peer support application',
    description = 'Find the course you teach, then tell us about your section and the support you are asking for.',
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const [selected, setSelected] = useState(null);

    const start = useCallback(
        (course) => {
            // The course travels as an id in the query string, which is what
            // step 1 reads. A stale or unreachable id is step 1's to report,
            // since the same parameter arrives from links this panel never
            // rendered.
            navigate(`/applications/new?course=${course._id}`, {
                state: cameFromState(location),
            });
        },
        [navigate, location]
    );

    return (
        <Card className="border-0 shadow-sm">
            <Card.Body>
                <h2 className="h5 mb-1">{heading}</h2>
                <p className="text-muted small mb-3">{description}</p>
                <CourseTypeahead
                    termCode={termCode}
                    label="Which course do you teach?"
                    placeholder="Start typing a course code or title, such as CHE 1800"
                    onSelect={setSelected}
                    // Editing the box after choosing means the chosen course
                    // is no longer what the reader is looking at. Without
                    // this the button stays enabled on the old course and
                    // starts an application for a course they moved away
                    // from, while the input reads something else.
                    onQueryChange={() => setSelected(null)}
                />
                <Button
                    variant="primary"
                    className="mt-3"
                    disabled={!selected}
                    onClick={() => selected && start(selected)}
                >
                    {selected
                        ? `Start application for ${selected.subject} ${selected.courseNumber}${selected.section ? ` section ${selected.section}` : ''}`
                        : 'Start application'}
                </Button>
            </Card.Body>
        </Card>
    );
}

StartApplicationPanel.propTypes = {
    /** Term whose catalog is searched. */
    termCode: PropTypes.string.isRequired,
    heading: PropTypes.string,
    description: PropTypes.string,
};

export default StartApplicationPanel;
